// Industry Solutions data for the consultative industry navigator
// Each industry maps to operational pain points, Portico solutions, business benefits,
// recommended products with rich attributes, and traits used for the Compare module.

export const industrySolutions = [
  {
    id: "corporate-offices",
    title: "Corporate Offices",
    iconName: "Briefcase",
    descriptor: "Acoustic comfort • Brand impression",
    image: "/Assets/images/industries/corporate-offices.jpg",
    overview:
      "Flooring systems engineered for executive environments where acoustic comfort, professional aesthetics, and weekend-only renovation windows are non-negotiable.",
    problems: [
      "Noise transfer in open office layouts disrupts productivity",
      "Renovations must complete over weekends without business disruption",
      "Flooring needs to elevate brand impression for client-facing spaces",
      "Heavy rolling chair traffic crushes pile in conference rooms"
    ],
    solutions: [
      "Acoustic-backed carpet tile reduces footfall noise and HVAC reverb",
      "Modular tile systems allow phased after-hours installation",
      "Premium aesthetic textures deliver an executive look at scale",
      "Reinforced backing maintains pile recovery under chair casters"
    ],
    benefits: [
      "Improve open-plan productivity and meeting acoustics",
      "Avoid downtime — installs complete between Friday close and Monday open",
      "Reinforce brand perception in lobbies, boardrooms, and client zones",
      "Extend lifecycle in chair-mat zones without replacement"
    ],
    traits: {
      traffic: 4,
      moisture: 1,
      cleaning: 2,
      acoustic: 5,
      comfort: 4,
      installSpeed: 5
    },
    smartRecs: [
      "Acoustic-backed modular carpet tile for open floors",
      "Luxury vinyl plank for client-facing zones",
      "Walk-off entry tile for lobby moisture capture"
    ],
    productSlugs: [
      "portico-nop-customs",
      "cross-guard-tile-ctg",
      "nature-view-rustique-7x48"
    ]
  },
  {
    id: "grocery-convenience",
    title: "Grocery & Convenience",
    iconName: "ShoppingCart",
    descriptor: "24/7 traffic • Spill resistant",
    image: "/Assets/images/industries/grocery_cstores.jpg",
    overview:
      "Flooring engineered for relentless cart traffic, spilled liquids, tracked-in moisture, and short overnight remodel windows.",
    problems: [
      "Heavy rolling carts damage seams and crush carpet pile",
      "Constant moisture tracked through entryways creates slip risk",
      "Stockers run remodels overnight — hours of downtime aren’t acceptable",
      "Spilled liquids and sticky residue stain low-grade flooring"
    ],
    solutions: [
      "Cart-resistant tile and grid entry systems built for rolling loads",
      "Slip-resistant entry mats capture moisture before it migrates",
      "Modular fast-install systems support overnight remodel cycles",
      "Sealed, non-absorbent surfaces resist staining and clean fast"
    ],
    benefits: [
      "Reduce slip-and-fall liability in entry and produce zones",
      "Extend replacement cycles in high-cart-traffic aisles",
      "Maintain brand-grade appearance under constant wear",
      "Cut overnight remodel labor with snap-together systems"
    ],
    traits: {
      traffic: 5,
      moisture: 5,
      cleaning: 5,
      acoustic: 2,
      comfort: 3,
      installSpeed: 5
    },
    smartRecs: [
      "Zone 1 + Zone 2 entry defense systems",
      "Slip-resistant flooring at coolers, deli, produce",
      "Fast-install modular tile for overnight remodels"
    ],
    productSlugs: ["2n1-custom-logo-mat", "nature-view-rustique-6x36", "snap-trax-plus"]
  },
  {
    id: "retail",
    title: "Retailers",
    iconName: "Store",
    descriptor: "Brand-grade aesthetics • Quick turn",
    image: "/Assets/images/industries/retailers.jpg",
    overview:
      "Flooring that elevates the merchandising experience while withstanding heavy seasonal traffic and tight remodel windows.",
    problems: [
      "Generic flooring undercuts visual merchandising",
      "Seasonal traffic spikes accelerate wear in entry and checkout zones",
      "Multi-zone stores need cohesive transitions between departments",
      "Remodel timelines are dictated by store-construction calendars"
    ],
    solutions: [
      "Custom-print and patterned tile for brand-defining storytelling",
      "Wear-rated systems for high-impact entry, checkout, and fitting rooms",
      "Cohesive product family enables seamless multi-zone design",
      "Pre-staged modular kits ship fast for remodel sprints"
    ],
    benefits: [
      "Strengthen in-store brand expression and customer dwell time",
      "Reduce visible wear in highest-traffic zones",
      "Streamline rollouts across multi-store programs",
      "Hit construction deadlines with predictable install windows"
    ],
    traits: {
      traffic: 5,
      moisture: 3,
      cleaning: 4,
      acoustic: 3,
      comfort: 3,
      installSpeed: 5
    },
    smartRecs: [
      "Brand-printed tile or custom logo entry mats",
      "Wear-rated tile at entry, checkout, and fitting rooms",
      "Modular kits aligned to multi-store rollout calendars"
    ],
    productSlugs: ["dominator-tile", "rover-tiles-ctg", "spectra-print-hd"]
  },
  {
    id: "hospitality",
    title: "Hospitality",
    iconName: "UtensilsCrossed",
    descriptor: "Luxury aesthetics • Acoustic comfort",
    image: "/Assets/images/industries/hospitality.jpg",
    overview:
      "Flooring that delivers a memorable guest experience — quiet underfoot, visually warm, and resilient against luggage, food, and beverage exposure.",
    problems: [
      "Hard surfaces echo in lobbies and corridors",
      "Luggage wheels and service carts dent and scuff softer flooring",
      "Spilled food, beverage, and cleaning agents stain low-grade vinyl",
      "Renovations must happen with rooms occupied above"
    ],
    solutions: [
      "Acoustic-backed luxury vinyl absorbs sound in corridors and lobbies",
      "High-density wear layers shrug off luggage and cart traffic",
      "Sealed, easy-clean surfaces protect against staining",
      "Modular systems install floor-by-floor with minimal disruption"
    ],
    benefits: [
      "Improve guest reviews tied to noise and quality perception",
      "Reduce premature replacement cycles in corridors",
      "Maintain luxury appearance against daily F&B exposure",
      "Renovate occupied properties without revenue loss"
    ],
    traits: {
      traffic: 4,
      moisture: 3,
      cleaning: 4,
      acoustic: 5,
      comfort: 5,
      installSpeed: 4
    },
    smartRecs: [
      "Acoustic-backed luxury vinyl for corridors and rooms",
      "Premium entry systems for porte-cochère and lobby",
      "Sealed surfaces for restaurant and lounge zones"
    ],
    productSlugs: ["nature-view-rustique-7x48", "s-trax-plus"]
  },
  {
    id: "fitness",
    title: "Fitness & Gymnasiums",
    iconName: "Dumbbell",
    descriptor: "Impact absorption • Heavy equipment",
    image: "/Assets/images/solutions/fitness-gymnasiums.jpg",
    overview:
      "Flooring engineered to absorb dropped weights, isolate vibration, and stand up to platform rigs, treadmills, and intense daily use.",
    problems: [
      "Dropped weights crack tile and damage subfloors",
      "Equipment vibration transmits to neighboring tenants",
      "Heavy machine load points compress under-rated foam",
      "Sweat and disinfectant degrade non-rubber surfaces"
    ],
    solutions: [
      "High-density rubber tile absorbs impact and protects subfloor",
      "Acoustic underlayments isolate vibration from adjacent units",
      "Engineered load-rated tiles support permanent rigs and racks",
      "Chemical-resistant rubber stands up to sweat and cleaners"
    ],
    benefits: [
      "Protect subfloors and reduce facility damage claims",
      "Avoid noise complaints in mixed-use buildings",
      "Extend equipment-zone lifecycle under heavy use",
      "Maintain hygiene without surface degradation"
    ],
    traits: {
      traffic: 4,
      moisture: 4,
      cleaning: 5,
      acoustic: 5,
      comfort: 5,
      installSpeed: 3
    },
    smartRecs: [
      "Heavy-rubber platform tiles for free-weight zones",
      "Acoustic-backed rubber for cardio decks",
      "Slip-resistant rubber for locker and shower edges"
    ],
    productSlugs: [
      "nature-view-rustique-7x48",
      "elements-100-series",
      "monster-tile-100-350-950-series-rrf"
    ]
  },
  {
    id: "mass-merchandiser",
    title: "Mass Merchandiser",
    iconName: "Warehouse",
    descriptor: "Big-box scale • Cost efficiency",
    image: "/Assets/images/solutions/mass-merchandiser.jpg",
    overview:
      "Flooring built for big-box scale — wide aisles, pallet jacks, forklifts, and demanding lifecycle cost expectations.",
    problems: [
      "Pallet jacks and forklifts gouge softer surfaces",
      "Square-footage scale makes premium floors cost-prohibitive",
      "Wide-open spans amplify echo and noise",
      "Multi-store rollouts require consistent install timelines"
    ],
    solutions: [
      "Industrial-grade tile rated for rolling industrial loads",
      "Cost-optimized commercial systems engineered for scale",
      "Acoustic options for break rooms and customer service zones",
      "Standardized modular kits keep rollouts predictable"
    ],
    benefits: [
      "Lower cost per square foot across the lifecycle",
      "Eliminate forklift-related floor damage",
      "Hit aggressive multi-store rollout deadlines",
      "Reduce mid-lifecycle patchwork repairs"
    ],
    traits: {
      traffic: 5,
      moisture: 3,
      cleaning: 4,
      acoustic: 2,
      comfort: 2,
      installSpeed: 5
    },
    smartRecs: [
      "Industrial-rated tile for sales floor and back-of-house",
      "Branded entry mats at every door",
      "Standardized modular kits for chain-wide rollouts"
    ],
    productSlugs: [
      "premier-nop-customs-clm",
      "frontrunner-entry",
      "diamond-plate-safety-step-iaf"
    ]
  },
  {
    id: "education",
    title: "Education & Financial",
    iconName: "GraduationCap",
    descriptor: "Quiet zones • Long lifecycle",
    image: "/Assets/images/solutions/education-financial.jpg",
    overview:
      "Flooring for schools, universities, banks, and credit unions where long lifecycles, acoustic comfort, and budget discipline all matter.",
    problems: [
      "Classroom and teller-line acoustics affect comprehension",
      "Public-funded budgets demand long replacement cycles",
      "High student or branch traffic accelerates wear",
      "Strict safety and slip-resistance standards apply"
    ],
    solutions: [
      "Acoustic-backed tile for classrooms and customer floors",
      "Long-lifecycle commercial systems with proven warranty data",
      "High-traffic-rated surfaces in corridors and lobbies",
      "Slip-rated and ADA-compliant surfaces throughout"
    ],
    benefits: [
      "Lower lifetime cost per square foot",
      "Pass safety audits without retrofit work",
      "Improve learning and customer experience through acoustic control",
      "Stretch capital budgets through extended replacement cycles"
    ],
    traits: {
      traffic: 4,
      moisture: 2,
      cleaning: 3,
      acoustic: 5,
      comfort: 4,
      installSpeed: 4
    },
    smartRecs: [
      "Acoustic-rated classroom and lobby tile",
      "ADA-compliant slip-rated entry systems",
      "Long-lifecycle commercial tile for corridors"
    ],
    productSlugs: ["premier-nop-tile-rg", "spectra-print-hd", "snap-trax-tile"]
  },
  {
    id: "government",
    title: "Government",
    iconName: "Landmark",
    descriptor: "ADA compliant • Long lifecycle",
    image: "/Assets/images/solutions/government.jpg",
    overview:
      "Flooring engineered for federal, state, and municipal facilities where ADA compliance, security, and decades-long lifecycle expectations are mandatory.",
    problems: [
      "ADA, slip, and accessibility standards must be met without exception",
      "Lifecycle expectations span decades, not years",
      "Security checkpoints and lobbies see extreme dwell traffic",
      "Procurement cycles favor proven, low-risk specifications"
    ],
    solutions: [
      "ADA-compliant slip-rated surfaces throughout",
      "Heavy commercial tile with documented lifecycle performance",
      "Reinforced systems for security checkpoints and queue lines",
      "Standardized GSA-friendly product families"
    ],
    benefits: [
      "Pass federal accessibility and safety audits",
      "Maximize taxpayer ROI through long replacement cycles",
      "Reduce maintenance overhead in checkpoint zones",
      "Simplify procurement and bid documentation"
    ],
    traits: {
      traffic: 4,
      moisture: 2,
      cleaning: 3,
      acoustic: 4,
      comfort: 3,
      installSpeed: 3
    },
    smartRecs: [
      "ADA-compliant entry and lobby systems",
      "Heavy commercial tile rated for 20+ year lifecycle",
      "Security-checkpoint reinforced surfaces"
    ],
    productSlugs: ["kensington-tile", "nature-view-18x36-lvt", "pinnacle-hd-custom"]
  },
  {
    id: "healthcare",
    title: "Pharmacy & Drugstore",
    iconName: "Pill",
    descriptor: "Hygienic • Fast cleaning",
    image: "/Assets/images/solutions/pharmacy-drugstore.jpg",
    overview:
      "Flooring engineered for pharmacy retail and drugstore environments where hygiene, slip resistance, and fast turnover all matter.",
    problems: [
      "Hygiene standards require seamless, non-absorbent surfaces",
      "Spilled liquids and tracked-in moisture create slip risk",
      "Daily janitorial cycles must be fast and chemical-tolerant",
      "Brand consistency across hundreds of locations is critical"
    ],
    solutions: [
      "Sealed, non-absorbent tile that resists bacterial growth",
      "Slip-rated surfaces in pharmacy counter and entry zones",
      "Chemical-resistant flooring tolerant of hospital-grade cleaners",
      "Standardized SKUs enabling chain-wide brand consistency"
    ],
    benefits: [
      "Pass health and sanitation inspections",
      "Reduce slip-and-fall liability around counters",
      "Cut nightly cleaning labor with fast-clean surfaces",
      "Maintain a uniform look across the chain"
    ],
    traits: {
      traffic: 4,
      moisture: 4,
      cleaning: 5,
      acoustic: 2,
      comfort: 3,
      installSpeed: 4
    },
    smartRecs: [
      "Sealed, slip-rated entry and counter zones",
      "Chemical-resistant tile for daily cleaning cycles",
      "Standardized chain SKUs for brand consistency"
    ],
    productSlugs: ["superior-nop-rg", "senator-softstep-plus-ctg", "frontrunner-sb-plus"]
  },
  {
    id: "specialty",
    title: "Specialty Stores",
    iconName: "Sparkles",
    descriptor: "Distinctive aesthetics • Brand storytelling",
    image: "/Assets/images/solutions/specialty-stores.jpg",
    overview:
      "Flooring that tells your brand story — distinctive textures, custom patterns, and durability that holds up to specialty retail traffic.",
    problems: [
      "Stock flooring fails to differentiate boutique experiences",
      "Custom designs traditionally carry long lead times",
      "Specialty traffic patterns concentrate wear in narrow zones",
      "Visual merchandising changes faster than flooring lifecycles"
    ],
    solutions: [
      "Custom logo and pattern programs with predictable lead times",
      "Modular tile lets you replace high-wear zones without full re-floor",
      "Texture libraries that pair with seasonal merchandising",
      "Premium aesthetics rated for retail durability"
    ],
    benefits: [
      "Differentiate the in-store experience and dwell time",
      "Avoid full-floor replacement when wear concentrates",
      "Refresh aesthetics without remodel-grade cost",
      "Extend visual life across merchandising cycles"
    ],
    traits: {
      traffic: 3,
      moisture: 2,
      cleaning: 3,
      acoustic: 3,
      comfort: 4,
      installSpeed: 4
    },
    smartRecs: [
      "Custom-printed logo entries and signature tile",
      "Modular swap-in tile for high-wear zones",
      "Premium luxury vinyl for transition spaces"
    ],
    productSlugs: ["portico-nop-customs", "snap-trax-plus", "luxury-step"]
  }
];

// Trait scale labels for the Compare module
export const traitMeta = {
  traffic: { label: "Traffic Level", icon: "Activity" },
  moisture: { label: "Moisture Exposure", icon: "Droplets" },
  cleaning: { label: "Cleaning Demand", icon: "Sparkles" },
  acoustic: { label: "Acoustic Need", icon: "Volume2" },
  comfort: { label: "Comfort Underfoot", icon: "Heart" },
  installSpeed: { label: "Install Speed Priority", icon: "Zap" }
};

// Product attribute enrichment: how a product fits its industry
export const productFitDetails = {
  // Grocery
  "2n1-custom-logo-mat": {
    bestFor: "Branded entry capture in grocery vestibules",
    primaryBenefit: "Stops moisture and dirt before it reaches aisles",
    whyFit: "Engineered for cart traffic with brand-printable insert.",
    durability: "High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "High",
    warranty: "5 yr",
    acoustic: "Low"
  },
  "snap-trax-plus": {
    bestFor: "High-traffic grocery and convenience entries",
    primaryBenefit: "Drains moisture below the walking surface",
    whyFit: "Open-channel grid catches debris while keeping floor walkable.",
    durability: "Very High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "Very High",
    warranty: "10 yr",
    acoustic: "Low"
  },
  "snap-trax-tile": {
    bestFor: "School and institutional entries",
    primaryBenefit: "Modular tiles snap together for fast installs",
    whyFit: "Replace single tiles in high-wear zones without full re-floor.",
    durability: "High",
    maintenance: "Low",
    installSpeed: "Very Fast",
    waterResistance: "High",
    warranty: "5 yr",
    acoustic: "Medium"
  },
  // Corporate
  "portico-nop-customs": {
    bestFor: "Corporate lobbies and brand-forward entries",
    primaryBenefit: "Custom logo, low-profile, scrape-grade fibers",
    whyFit: "Reinforces brand identity at point of arrival.",
    durability: "High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "Medium",
    warranty: "5 yr",
    acoustic: "Medium"
  },
  "cross-guard-tile-ctg": {
    bestFor: "Corporate corridors and conference rooms",
    primaryBenefit: "Acoustic-rated tile reduces footfall and chair noise",
    whyFit: "Pile recovers under chair casters and rolling traffic.",
    durability: "High",
    maintenance: "Medium",
    installSpeed: "Fast",
    waterResistance: "Medium",
    warranty: "10 yr",
    acoustic: "High"
  },
  "nature-view-rustique-7x48": {
    bestFor: "Hospitality, fitness, and corporate transition zones",
    primaryBenefit: "Premium LVT aesthetic with acoustic backing options",
    whyFit: "Wide-plank wood look without the maintenance overhead.",
    durability: "Very High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "Very High",
    warranty: "15 yr",
    acoustic: "High"
  },
  "nature-view-rustique-6x36": {
    bestFor: "Grocery checkout lines and convenience aisles",
    primaryBenefit: "Slip-rated LVT that holds up to cart traffic",
    whyFit: "Sealed wear-layer cleans fast under nightly maintenance.",
    durability: "High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "Very High",
    warranty: "10 yr",
    acoustic: "Medium"
  },
  "nature-view-18x36-lvt": {
    bestFor: "Government corridors and high-dwell lobbies",
    primaryBenefit: "Large-format LVT with stone-look durability",
    whyFit: "Long lifecycle and easy patch-replace in damaged areas.",
    durability: "Very High",
    maintenance: "Low",
    installSpeed: "Medium",
    waterResistance: "Very High",
    warranty: "15 yr",
    acoustic: "Medium"
  },
  // Retail
  "dominator-tile": {
    bestFor: "Retail entry, checkout, and aisle traffic",
    primaryBenefit: "Carpet tile rated for crushing rolling loads",
    whyFit: "Holds appearance under 5x daily cart and stroller traffic.",
    durability: "Very High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "Medium",
    warranty: "10 yr",
    acoustic: "Medium"
  },
  "rover-tiles-ctg": {
    bestFor: "Pet retail and high-spill specialty zones",
    primaryBenefit: "Stain-shielded fibers shrug off accidents",
    whyFit: "Sealed back prevents wicking into adjacent tiles.",
    durability: "High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "High",
    warranty: "10 yr",
    acoustic: "Medium"
  },
  "spectra-print-hd": {
    bestFor: "Brand-printed retail and education environments",
    primaryBenefit: "Custom prints embedded in heavy-traffic carpet",
    whyFit: "Tells your brand story without sacrificing wear life.",
    durability: "High",
    maintenance: "Medium",
    installSpeed: "Fast",
    waterResistance: "Medium",
    warranty: "10 yr",
    acoustic: "High"
  },
  // Hospitality
  "s-trax-plus": {
    bestFor: "Hotel porte-cochère and luggage-cart entries",
    primaryBenefit: "Aluminum grid handles luggage wheels and luggage carts",
    whyFit: "Captures water and grit without scuffing front-of-house finishes.",
    durability: "Very High",
    maintenance: "Low",
    installSpeed: "Medium",
    waterResistance: "Very High",
    warranty: "10 yr",
    acoustic: "Low"
  },
  // Mass merchandiser
  "premier-nop-customs-clm": {
    bestFor: "Big-box branded entry mats",
    primaryBenefit: "Custom logo capture at scale",
    whyFit: "Standardized SKU rolls out across hundreds of stores.",
    durability: "High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "Medium",
    warranty: "5 yr",
    acoustic: "Low"
  },
  "frontrunner-entry": {
    bestFor: "High-traffic retail and big-box vestibules",
    primaryBenefit: "Aggressive scraping and moisture trap",
    whyFit: "Captures bulk debris before it migrates to sales floor.",
    durability: "Very High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "Very High",
    warranty: "10 yr",
    acoustic: "Low"
  },
  "diamond-plate-safety-step-iaf": {
    bestFor: "Industrial back-of-house and stockroom",
    primaryBenefit: "Slip-rated industrial surface for staff zones",
    whyFit: "Anti-fatigue underfoot during long shifts.",
    durability: "Very High",
    maintenance: "Low",
    installSpeed: "Medium",
    waterResistance: "High",
    warranty: "5 yr",
    acoustic: "Low"
  },
  // Education
  "premier-nop-tile-rg": {
    bestFor: "School entries and corridor scrape zones",
    primaryBenefit: "Modular roll-goods install with low waste",
    whyFit: "Predictable lifecycle simplifies capital planning.",
    durability: "High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "Medium",
    warranty: "10 yr",
    acoustic: "Medium"
  },
  // Government
  "kensington-tile": {
    bestFor: "Federal and municipal corridors",
    primaryBenefit: "Heritage aesthetic with commercial-grade lifecycle",
    whyFit: "GSA-friendly spec with documented warranty data.",
    durability: "Very High",
    maintenance: "Medium",
    installSpeed: "Medium",
    waterResistance: "Medium",
    warranty: "15 yr",
    acoustic: "High"
  },
  "pinnacle-hd-custom": {
    bestFor: "Government lobbies and ceremonial zones",
    primaryBenefit: "Custom-design heavy-duty broadloom",
    whyFit: "Bespoke pattern with HD wear rating.",
    durability: "Very High",
    maintenance: "Medium",
    installSpeed: "Medium",
    waterResistance: "Medium",
    warranty: "15 yr",
    acoustic: "High"
  },
  // Pharmacy / drugstore
  "superior-nop-rg": {
    bestFor: "Pharmacy entry and counter zones",
    primaryBenefit: "Sealed, low-profile, slip-rated walk-off",
    whyFit: "Tolerates daily disinfectant cleaning cycles.",
    durability: "High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "High",
    warranty: "10 yr",
    acoustic: "Low"
  },
  "senator-softstep-plus-ctg": {
    bestFor: "Pharmacy retail aisles",
    primaryBenefit: "Cushion-back tile improves staff comfort",
    whyFit: "Reduces fatigue at counter and pickup zones.",
    durability: "High",
    maintenance: "Medium",
    installSpeed: "Fast",
    waterResistance: "Medium",
    warranty: "10 yr",
    acoustic: "High"
  },
  "frontrunner-sb-plus": {
    bestFor: "Pharmacy and drugstore vestibules",
    primaryBenefit: "Aggressive moisture and grit capture",
    whyFit: "Pairs with sealed Zone 3 floor for full Zone Defense.",
    durability: "Very High",
    maintenance: "Low",
    installSpeed: "Fast",
    waterResistance: "Very High",
    warranty: "10 yr",
    acoustic: "Low"
  },
  // Fitness
  "elements-100-series": {
    bestFor: "Cardio decks and group-fitness floors",
    primaryBenefit: "Vibration-isolating rubber backing",
    whyFit: "Isolates equipment vibration from neighboring tenants.",
    durability: "Very High",
    maintenance: "Low",
    installSpeed: "Medium",
    waterResistance: "High",
    warranty: "10 yr",
    acoustic: "Very High"
  },
  "monster-tile-100-350-950-series-rrf": {
    bestFor: "Free-weight platforms and CrossFit boxes",
    primaryBenefit: "Heavy rubber tile absorbs dropped weights",
    whyFit: "Protects subfloor in dedicated lift zones.",
    durability: "Very High",
    maintenance: "Low",
    installSpeed: "Medium",
    waterResistance: "High",
    warranty: "15 yr",
    acoustic: "Very High"
  },
  // Specialty
  "luxury-step": {
    bestFor: "Boutique entries and specialty merchandising",
    primaryBenefit: "Premium aesthetic with retail-grade durability",
    whyFit: "Distinctive surface holds up to specialty traffic.",
    durability: "High",
    maintenance: "Medium",
    installSpeed: "Fast",
    waterResistance: "Medium",
    warranty: "5 yr",
    acoustic: "Medium"
  }
};
